import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/free-solid-svg-icons';
const PANEL_CLASS = 'absolute z-40 mt-1 max-h-72 w-full overflow-auto rounded-lg ' +
    'border border-base-300 bg-base-100 py-1 shadow-lg';
const byCode = (a, b) => a.code.localeCompare(b.code);
// Matches on both the code and its label so a salesperson can find a corporate
// code by company name. An empty query lists every suggestion. Results are
// ordered alphabetically by code.
const matches = (suggestions, query) => {
    const trimmed = query.trim().toLowerCase();
    const pool = trimmed
        ? suggestions.filter(suggestion => suggestion.code.toLowerCase().includes(trimmed) ||
            (suggestion.label?.toLowerCase().includes(trimmed) ?? false))
        : suggestions;
    return [...pool].sort(byCode);
};
// The promo/corporate code field. A combobox: it suggests the codes configured
// for the selected property (or market), but still accepts free text so a code
// Apaleo has not surfaced can be entered. The parent owns the code value and
// type; this holds only the dropdown open/active state.
export const CodeTypeahead = ({ codeType, onCodeTypeChange, value, onChange, suggestions, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const blurTimer = useRef();
    const results = useMemo(() => matches(suggestions, value), [
        suggestions,
        value
    ]);
    const commit = (suggestion) => {
        onChange(suggestion.code);
        setIsOpen(false);
    };
    const handleType = (next) => {
        onChange(next);
        setActiveIndex(0);
        setIsOpen(true);
    };
    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex(index => Math.min(index + 1, results.length - 1));
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(index => Math.max(index - 1, 0));
        }
        else if (event.key === 'Enter') {
            const suggestion = results[activeIndex];
            if (isOpen && suggestion) {
                event.preventDefault();
                commit(suggestion);
            }
        }
        else if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };
    const listboxId = 'code-typeahead-list';
    const hasSuggestions = suggestions.length > 0;
    return (_jsxs("div", { className: `relative ${className}`, onBlur: () => {
            // Delay so an option click (which blurs the input) can register first.
            blurTimer.current = setTimeout(() => setIsOpen(false), 100);
        }, onFocus: () => {
            if (blurTimer.current) {
                clearTimeout(blurTimer.current);
            }
        }, children: [_jsxs("div", { className: "flex h-12 items-center gap-2 rounded-lg border border-base-300 bg-base-100 px-3 lg:h-10 lg:px-2", children: [_jsx(FontAwesomeIcon, { icon: faTag, className: "text-cabana" }), _jsx("span", { className: "sr-only", children: "Code type" }), _jsxs("select", { "aria-label": "Code type", value: codeType, onChange: e => onCodeTypeChange(e.target.value), className: "bg-transparent text-sm outline-none", children: [_jsx("option", { value: "promo", children: "Promo" }), _jsx("option", { value: "corporate", children: "Corporate" })] }), _jsx("input", { type: "text", role: "combobox", "aria-expanded": isOpen, "aria-controls": listboxId, "aria-autocomplete": "list", "aria-label": "Promo or corporate code", placeholder: "Code (optional)", value: value, onChange: e => handleType(e.target.value), onFocus: () => setIsOpen(true), onKeyDown: handleKeyDown, className: "w-full bg-transparent text-sm outline-none" })] }), isOpen && hasSuggestions && results.length > 0 && (_jsx("ul", { id: listboxId, role: "listbox", className: PANEL_CLASS, children: results.map((suggestion, index) => {
                    const isActive = index === activeIndex;
                    return (_jsx("li", { children: _jsxs("button", { type: "button", role: "option", "aria-selected": suggestion.code === value, 
                            // Prevent the input blur from firing before onClick.
                            onMouseDown: e => e.preventDefault(), onClick: () => commit(suggestion), className: `flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm ${isActive
                                ? 'bg-pool text-midnight'
                                : 'text-midnight hover:bg-linen'}`, children: [_jsx("span", { className: "font-medium", children: suggestion.code }), suggestion.label && (_jsx("span", { className: "truncate text-xs text-light-midnight", children: suggestion.label }))] }) }, suggestion.code));
                }) }))] }));
};
